import {Icon} from '@wandb/weave/components/Icon';
import React, {createContext, useContext} from 'react';
import {Components} from 'react-virtuoso';

import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {FOOTER_ACTIONS_HEIGHT} from './constants';

/**
 * Context that determines whether Scroller reserves space for footer actions.
 *
 * Why use Context? The Scroller component must be stable across renders,
 * otherwise users will encounter unexpected behavior (e.g. scroll position
 * resetting, footer actions flickering, etc). Context is the most reliable
 * way to maintain a stable component reference while dynamically providing
 * changing values from parent state.
 */
export const FooterActionsVisibilityContext = createContext<boolean>(false);

/**
 * Scroller component that conditionally reserves space for footer actions.
 */
export const Scroller: Components['Scroller'] = React.forwardRef(
  (props, ref) => {
    const isFooterActionsVisible = useContext(FooterActionsVisibilityContext);
    const footerHeight = isFooterActionsVisible ? FOOTER_ACTIONS_HEIGHT : 0;
    return (
      <WorkspaceDrawerScroller
        ref={ref}
        footerHeight={footerHeight}
        {...props}
      />
    );
  }
);

/**
 * This component is used as Header/Footer on the virtuoso list to ensure
 * proper top/bottom spacing. Unfortunately I wasn't able to figure out a
 * plain CSS solution that played nicely with Virtuoso. My first instinct
 * was to add top/bottom padding to the Scroller container, but that didn't
 * affect the position of the inner items. Then, I tried adding top/bottom
 * margin to the first/last items in the list. The first item correctly
 * displayed a top margin, but the last item never showed its bottom margin.
 * Next, I tried setting top/bottom padding on a custom List component,
 * but then the scroll stopped working. Finally I resorted to this solution.
 * Though unideal, it's the only option I found that actually works without
 * breaking something else.
 * Related: https://github.com/petyosi/react-virtuoso/issues/238
 */
export const TopBottomSpacing = () => <div className="h-8" />;

export const EmptyPlaceholder = () => (
  <div className="flex h-[calc(100%_-_16px)] w-full flex-col items-center justify-center text-moon-500">
    <div className="mb-16 flex h-[3rem] w-[3rem] items-center justify-center rounded-full bg-moon-300/[0.48]">
      <Icon name="search" className="h-24 w-24" />
    </div>
    <p className="font-semibold">No results found</p>
    <p className="text-sm">Try another search.</p>
  </div>
);
